import { useBooleanState } from "../../core/hooks/useBooleanState";
import { IProcedureRowType } from "../../graphql/gpl-graphQLTypes.gen";
import { trackGPLEvent } from "../../providers/GoogleTagManager/GoogleTagManagerProvider";
import { IProcedureDetails, IProcedureRow } from "../../typings/filterEntities";
import { getProcedureStepsWithOffsprings } from "../../utils/procedure";
import Button from "../Button/Button";
import ExternalLink from "../ExternalLink/ExternalLink";
import Heading from "../Heading/Heading";
import ProcedureRowContainer from "../ProcedureRowContainer/ProcedureRowContainer";
import styles from "./ProcedureDetails.module.css";

function splitFormRows(rows: IProcedureRow[]) {
  const areHeadersUntilIndex = rows.findIndex((row) => row.type !== IProcedureRowType.Heading);
  return [
    areHeadersUntilIndex === -1 ? [] : rows.slice(0, areHeadersUntilIndex),
    rows.slice(areHeadersUntilIndex === -1 ? 0 : areHeadersUntilIndex, rows.length),
  ];
}

interface IProps {
  procedure: IProcedureDetails;
}

export default function ProcedureDetails({ procedure }: IProps) {
  const { rows, source, uid } = procedure;
  const [inBannerHeaderRows, belowBannerRows] = splitFormRows(rows ?? []);
  const stepsWithOffsprings = getProcedureStepsWithOffsprings(belowBannerRows);
  const hasOffsprings = stepsWithOffsprings.some((step) => step.offsprings?.length);

  const headersOnly =
    inBannerHeaderRows?.length === 0 && belowBannerRows?.[0]?.type === IProcedureRowType.Heading;

  const [offspringsVisible, , , toggleOffsprings] = useBooleanState(true);

  return (
    <section className={styles.root}>
      <header className={headersOnly ? styles.headersOnlyBanner : styles.banner}>
        <Heading className={styles.title} component="h2" variant="h3">
          {procedure.title}
        </Heading>
        {!!procedure.description && (
          <div className={styles.headerText}>{procedure.description}</div>
        )}
        {!!procedure.scoringEnabled && (
          <div className={styles.headerText}>Procedure Max Score: {procedure.maxScore}</div>
        )}
      </header>
      <div className={styles.steps}>
        {!!inBannerHeaderRows?.length && (
          <ul className={styles.list}>
            {inBannerHeaderRows.map((r) => (
              <Heading key={r.id} className={styles.title} component="h2" variant="h3">
                {r.label}
              </Heading>
            ))}
          </ul>
        )}
        {!!stepsWithOffsprings?.length && (
          <ul className={headersOnly ? styles.headersOnlyList : styles.list}>
            {hasOffsprings ? (
              <Button onClick={toggleOffsprings}>
                {offspringsVisible ? "Collapse all" : "Expand all"}
              </Button>
            ) : null}
            {stepsWithOffsprings.map((step) => {
              return (
                <ProcedureRowContainer
                  key={`step-${step.id}`}
                  step={step}
                  depth={1}
                  offspringsVisibleDefault={offspringsVisible}
                />
              );
            })}
          </ul>
        )}
      </div>
      <ProcedureSource source={source} uid={uid} />
    </section>
  );
}

function extractDomain(url: string) {
  try {
    if (typeof URL !== "undefined") {
      return new URL(url).hostname;
    }
  } catch {
    // ignore
  }
  return null;
}

function ProcedureSource({ source, uid }: Pick<IProcedureDetails, "source" | "uid">) {
  if (source?.url) {
    const domain = extractDomain(source.url);
    return (
      <div className={styles.source}>
        Source:{" "}
        <ExternalLink
          href={source.url}
          onClick={() =>
            trackGPLEvent("SeeExternalSource", {
              action: "Click",
              category: "Procedure",
              label: uid,
            })
          }
        >
          {source.name || source.url}
          {source.name && domain ? ` (${domain})` : null}
        </ExternalLink>
      </div>
    );
  } else if (source?.name) {
    return <div className={styles.source}>Source: {source.name}</div>;
  }
  return null;
}
